import { useRef } from 'react';
import {
  FieldValues,
  UseControllerProps,
  useController
} from 'react-hook-form';
import { Add } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  createFilterOptions,
  TextField
} from '@mui/material';
import get from 'lodash/get';

import {
  findStandardStaffTitle,
  OTHER_STAFF_TITLE_MAX_LENGTH,
  OTHER_STAFF_TITLE_MIN_LENGTH,
  STANDARD_STAFF_TITLES
} from '@/constants/staffTitles';

const filterStandardTitles = createFilterOptions<string>();

export const validateStaffTitle = (
  value: string | null | undefined,
  savedTitle?: string | null
): true | string => {
  if (!value || value === savedTitle || findStandardStaffTitle(value)) {
    return true;
  }
  const trimmed = value.trim();
  if (trimmed.length < OTHER_STAFF_TITLE_MIN_LENGTH) {
    return `Title must be at least ${OTHER_STAFF_TITLE_MIN_LENGTH} characters`;
  }
  if (trimmed.length > OTHER_STAFF_TITLE_MAX_LENGTH) {
    return `Title must be at most ${OTHER_STAFF_TITLE_MAX_LENGTH} characters`;
  }
  return true;
};

const toStoredTitle = (value: string | null): string | null => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return null;
  }
  return findStandardStaffTitle(trimmed) ?? trimmed;
};

export function ControlledStaffTitleAutocomplete<TFields extends FieldValues>({
  name,
  control,
  rules,
  label,
  placeholder,
  disabled
}: UseControllerProps<TFields> & {
  label: string;
  placeholder?: string;
  disabled?: boolean;
}) {
  const savedTitleRef = useRef<string | null>(null);
  const {
    field: { value, onChange, onBlur, ref },
    fieldState: { error },
    formState: { defaultValues }
  } = useController({
    name,
    control,
    rules: {
      ...rules,
      validate: (title) => validateStaffTitle(title, savedTitleRef.current)
    }
  });
  savedTitleRef.current = get(defaultValues, name) ?? null;

  return (
    <Autocomplete<string, false, false, true>
      freeSolo
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      autoHighlight
      id={`mui-component-textfield-${name}`}
      disabled={disabled}
      options={STANDARD_STAFF_TITLES}
      value={(value as string | null) || null}
      onChange={(_event, newValue) => onChange(toStoredTitle(newValue))}
      filterOptions={(options, state) => {
        const filtered = filterStandardTitles(options, state);
        const typed = state.inputValue.trim();
        if (
          typed.length >= OTHER_STAFF_TITLE_MIN_LENGTH &&
          !findStandardStaffTitle(typed)
        ) {
          filtered.push(typed);
        }
        return filtered;
      }}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props as typeof props & {
          key: string;
        };
        if (findStandardStaffTitle(option)) {
          return (
            <li key={key} {...optionProps}>
              {option}
            </li>
          );
        }
        return (
          <li key={`other-${key}`} {...optionProps}>
            <Add fontSize="small" sx={{ mr: 1 }} />
            Other title:
            <Box component="span" color="text.secondary" ml={0.5}>
              &quot;{option}&quot;
            </Box>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          inputRef={ref}
          onBlur={onBlur}
          label={label}
          placeholder={placeholder}
          error={!!error}
          helperText={error?.message}
          inputProps={{
            ...params.inputProps,
            maxLength: OTHER_STAFF_TITLE_MAX_LENGTH
          }}
        />
      )}
    />
  );
}
